import { isQuietMaintenanceFailure, maintenanceRetryAfter } from "./maintenance-retry";
import * as Sentry from "@sentry/react";
import { isCancelledError } from "@tanstack/react-query";
import { isAxiosError } from "axios";

/** Why a handled failure happened, sent as the Sentry `failure_reason` tag. */
export type FailureReason = "refused" | "offline" | "cancelled" | "timeout" | "server" | "client" | "unknown";

/** Non-identifying context only: no ids, names, phone numbers, tokens, or user text. */
export type FailureDetails = Record<string, string | number | boolean | undefined>;

// Refusals the server sends on purpose (unauthenticated, forbidden, gone, conflicting, invalid, throttled); the UI
// explains them.
const EXPECTED_REFUSAL_STATUSES = new Set([401, 403, 404, 409, 422, 429]);

// The backend reports its own 5xx to the same Sentry project; only the gateway errors it never sees are new.
const GATEWAY_STATUSES = new Set([502, 503, 504]);

// `fetch` rejects with these when the request never reached the server.
const NETWORK_FAILURE_MESSAGE = /failed to fetch|network ?error|load failed|networkerror when attempting to fetch/i;

const reported = new WeakSet<object>();

/** An HTTP failure that is not an axios error, for example an Inertia `httpException` or a `useHttp` response. */
type GatewayResponse = { status: number; headers?: unknown; maintenanceRetryExhausted?: boolean };

class HttpFailure extends Error {
  readonly response: GatewayResponse;

  constructor(response: GatewayResponse) {
    super(`Request failed with status ${response.status}`);
    this.name = "HttpFailure";
    // Keep only maintenance metadata, never cookies or the form response body.
    const retryAfter = maintenanceRetryAfter({ response });

    this.response = {
      status: response.status,
      headers: retryAfter === undefined ? undefined : { "retry-after": retryAfter },
      maintenanceRetryExhausted: response.maintenanceRetryExhausted,
    };
  }
}

// Axios errors, `HttpFailure`, and the tree loaders' HTTP errors all carry `response.status`.
const failedStatus = (error: unknown): number | undefined => {
  const response =
    typeof error === "object" && error !== null && "response" in error
      ? (error as { response?: { status?: unknown } }).response
      : undefined;

  return typeof response?.status === "number" ? response.status : undefined;
};

const isCancellation = (error: unknown) =>
  isCancelledError(error) ||
  (isAxiosError(error) && error.code === "ERR_CANCELED") ||
  (error instanceof Error && error.name === "AbortError");

const isTimeout = (error: unknown) =>
  isAxiosError(error) && (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT");

const neverReachedServer = (error: unknown) =>
  isAxiosError(error) ? !error.response : error instanceof Error && NETWORK_FAILURE_MESSAGE.test(error.message);

export const classifyFailure = (error: unknown): { reason: FailureReason; status?: number } => {
  const status = failedStatus(error);

  if (status !== undefined) {
    if (EXPECTED_REFUSAL_STATUSES.has(status)) {
      return { reason: "refused", status };
    }

    return { reason: status >= 500 ? "server" : "client", status };
  }

  if (isCancellation(error)) {
    return { reason: "cancelled" };
  }

  if (navigator.onLine === false) {
    return { reason: "offline" };
  }

  // Only a few requests set a deadline (voice proposals, link resolution); missing it means the server was too slow.
  if (isTimeout(error)) {
    return { reason: "timeout" };
  }

  if (neverReachedServer(error)) {
    return { reason: "offline" };
  }

  return { reason: "unknown" };
};

const toError = (error: unknown) => (error instanceof Error ? error : new Error(String(error)));

/**
 * The one way the web app reports a failure it handled itself (a toast, an error state, an empty result):
 * - expected refusals (401/403/404/409/422/429) report nothing, because the UI explains them;
 * - offline and cancelled failures leave a Sentry breadcrumb only;
 * - everything else goes to Sentry with `failure_kind`/`failure_status`/`failure_reason` tags.
 * Each error object is reported once, so a call site and the global React Query handlers never double up.
 * `kind` is a stable slug such as `gallery.photo_upload`; `details` must not identify anyone.
 */
export const reportHandledFailure = (kind: string, error: unknown, details: FailureDetails = {}): void => {
  if (isQuietMaintenanceFailure(error)) {
    return;
  }

  if (typeof error === "object" && error !== null) {
    if (reported.has(error)) {
      return;
    }

    reported.add(error);
  }

  const { reason, status } = classifyFailure(error);

  if (reason === "refused") {
    return;
  }

  if (reason === "offline" || reason === "cancelled") {
    Sentry.addBreadcrumb({
      category: "handled_failure",
      level: "warning",
      message: kind,
      data: { reason, ...details },
    });

    return;
  }

  const context = Object.fromEntries(
    Object.entries(details).filter((entry): entry is [string, string | number | boolean] => entry[1] !== undefined),
  );

  Sentry.captureException(toError(error), {
    tags: { failure_kind: kind, failure_reason: reason, ...(status ? { failure_status: String(status) } : {}) },
    extra: context,
  });
};

/** For `useHttp` and Inertia visits: the backend already reports 5xx, so only gateway statuses are reported here. */
export const reportGatewayFailure = (kind: string, response: GatewayResponse, details: FailureDetails = {}): void => {
  if (GATEWAY_STATUSES.has(response.status)) {
    reportHandledFailure(kind, new HttpFailure(response), details);
  }
};

// Path segments that can identify someone (numeric ids, share tokens, UUIDs) are masked so routes group by shape.
const IDENTIFYING_SEGMENT = /\d|^[A-Za-z0-9_-]{20,}$/;

/** The request's route with identifying segments masked, for example `post /nodes/:id/children`. */
export const failedRequestRoute = (error: unknown): string | undefined => {
  if (!isAxiosError(error) || !error.config?.url) {
    return undefined;
  }

  const path = error.config.url.split("?")[0].replace(/^https?:\/\/[^/]+/, "");
  const route = path
    .split("/")
    .map((segment) => (IDENTIFYING_SEGMENT.test(segment) ? ":id" : segment))
    .join("/");

  return `${(error.config.method ?? "get").toLowerCase()} ${route}`;
};

const keyName = (key: ReadonlyArray<unknown>) =>
  key
    .filter((segment): segment is string => typeof segment === "string")
    .slice(0, 2)
    .join(".");

/** Global `QueryCache` `onError`: every query that ends in an error state reports through `reportHandledFailure`. */
export const reportQueryFailure = (
  error: unknown,
  query: { queryKey: ReadonlyArray<unknown>; meta?: { failureKind?: string } },
): void => {
  reportHandledFailure(query.meta?.failureKind ?? `query:${keyName(query.queryKey)}`, error, {
    route: failedRequestRoute(error),
  });
};

/** Global `MutationCache` `onError`: every failed mutation reports through `reportHandledFailure`. */
export const reportMutationFailure = (
  error: unknown,
  mutation: { options: { mutationKey?: ReadonlyArray<unknown> }; meta?: { failureKind?: string } },
): void => {
  const route = failedRequestRoute(error);
  const key = keyName(mutation.options.mutationKey ?? []);

  reportHandledFailure(mutation.meta?.failureKind ?? `mutation:${key || route || "unknown"}`, error, { route });
};
